'use client';
import { ProductIcon } from '../components/product-icon';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, Rocket } from 'lucide-react';
import { AppLink as Link, post, event, analyticsStatus } from '@curio/client';
import { ButtonLink } from '../components/button-link';
import type { Account, Product } from '@curio/contracts';
import { levelBonusPercent } from '@curio/domain';
import { Badge, Button, ErrorMessage, Loading, RatingStars } from '@curio/ui';
import { compact } from '../components/live-number';

const statusOrder = { published: 0, draft: 1, suspended: 2 };
const byStatusThenNewest = (a: Product, b: Product) =>
  statusOrder[a.status] - statusOrder[b.status] ||
  Date.parse(b.createdAt) - Date.parse(a.createdAt);
const statusLabel = {
  draft: 'Draft',
  published: 'Published',
  suspended: 'Suspended',
};

export function ProjectList({
  account,
  projects,
  pending,
}: {
  account: Account;
  projects: Product[];
  pending: boolean;
}) {
  const client = useQueryClient();
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState<string | null>(null);
  const rules = account.rules;
  const bonus = levelBonusPercent(account.level, rules);
  const boost = async (product: Product) => {
    setBusy(product.id);
    setError(undefined);
    try {
      await post(`/products/${product.id}/boost`, {
        idempotencyKey: crypto.randomUUID(),
      });
      await client.invalidateQueries();
    } catch (e) {
      event('action_failed', product.id, {
        action: 'boost_purchase',
        status_code: analyticsStatus(e),
      });
      setError(e);
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="panel projects" aria-label="My projects">
      <div className="projects-head">
        <div>
          <h2>My projects</h2>
          <p>
            {projects.length
              ? `Your level adds ${bonus}% to your published products’ discovery ranking.`
              : 'Publish a product to track its rating, reviews, and saves here.'}
          </p>
        </div>
        {account.unlocked && (
          <ButtonLink href="/submit" size="sm">
            Add a product
          </ButtonLink>
        )}
      </div>
      {pending ? (
        <Loading />
      ) : projects.length ? (
        <div className="projects-list">
          {[...projects].sort(byStatusThenNewest).map((p) => (
            <ProjectRow
              key={p.id}
              product={p}
              credits={account.credits}
              busy={busy === p.id}
              onBoost={() => void boost(p)}
            />
          ))}
        </div>
      ) : account.unlocked ? (
        <div className="projects-empty">
          <h3>No projects yet</h3>
          <p>
            Submit an app, website, or tool you built. Once it is published you
            can boost it into the homepage spotlight with the credits you earn
            from reviews.
          </p>
          <ButtonLink href="/submit">Add a product</ButtonLink>
        </div>
      ) : (
        <div className="projects-empty">
          <h3>Submissions unlock after two reviews</h3>
          <p>
            Review two different products to unlock submissions and claims. Your
            credits remain available for boosts.
          </p>
          <Link className="reviewer-cta" href="#review-suggestions">
            Find a product to review{' '}
            <ArrowUpRight size={14} aria-hidden="true" />
          </Link>
        </div>
      )}
      <ErrorMessage error={error} />
      {projects.length > 0 && (
        <p className="projects-foot">
          Each boost gives one product {rules.boostHours} hours in the homepage
          spotlight and costs 1 credit. You have{' '}
          {account.credits === 1 ? '1 credit' : `${account.credits} credits`}.
        </p>
      )}
    </section>
  );
}

function ProjectRow({
  product: p,
  credits,
  busy,
  onBoost,
}: {
  product: Product;
  credits: number;
  busy: boolean;
  onBoost: () => void;
}) {
  const boostEnd = p.boostUntil ? Date.parse(p.boostUntil) : 0;
  const boosted = boostEnd > Date.now();
  const hoursLeft = Math.max(1, Math.ceil((boostEnd - Date.now()) / 3600000));
  const live = p.verified && p.status === 'published';
  return (
    <div className="project-row">
      <div className="project-main">
        <Link href={'/products/' + p.slug} aria-label={`View ${p.name}`}>
          <ProductIcon product={p} size={52} />
        </Link>
        <div className="project-copy">
          <div className="project-name">
            <Link href={'/products/' + p.slug}>{p.name}</Link>
            <Badge tone={p.status === 'published' ? 'trust' : 'neutral'}>
              {statusLabel[p.status]}
            </Badge>
            {!p.verified && (
              <span className="caption muted">Ownership not verified</span>
            )}
            {boosted && (
              <Badge tone="xp">
                <Rocket size={12} aria-hidden="true" /> Boosted · {hoursLeft}h
                left
              </Badge>
            )}
          </div>
          <div className="project-tagline">{p.tagline}</div>
        </div>
      </div>
      <div className="project-stats">
        <div className="project-stat">
          <RatingStars value={p.rating} />
          <span>Rating</span>
        </div>
        <div className="project-stat">
          <strong>{p.reviewCount.toLocaleString()}</strong>
          <span>{p.reviewCount === 1 ? 'Review' : 'Reviews'}</span>
        </div>
        <div className="project-stat">
          <strong>{compact(p.saveCount)}</strong>
          <span>Saves</span>
        </div>
      </div>
      <div className="project-actions">
        {p.verified && (
          <ButtonLink
            href={'/products/' + p.slug + '#feedback'}
            variant="outline"
            size="sm"
          >
            View feedback
          </ButtonLink>
        )}
        {live && (
          <Button
            size="sm"
            variant="tint"
            className="project-boost"
            icon={<Rocket size={15} aria-hidden="true" />}
            disabled={credits < 1 || busy}
            onClick={onBoost}
          >
            {busy
              ? 'Boosting…'
              : boosted
                ? 'Extend ⋅ 1 credit'
                : 'Boost · 1 credit'}
          </Button>
        )}
        <ButtonLink href={'/submit?edit=' + p.slug} variant="outline" size="sm">
          Edit
        </ButtonLink>
      </div>
    </div>
  );
}
